import type { ReactElement, ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useAuthHydrated, useAuthStore, useIsHeadManager, useIsSalesDirector } from '@/store/auth.store';
import Layout from '@/components/Layout';
import InstallPrompt from '@/components/InstallPrompt';
import ScrollManager from '@/components/ScrollManager';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Establishments from '@/pages/Establishments';
import EstablishmentDetail from '@/pages/EstablishmentDetail';
import Managers from '@/pages/Managers';
import ManagerCard from '@/pages/ManagerCard';
import MyCabinet from '@/pages/MyCabinet';
import Payouts from '@/pages/Payouts';
import Chat from '@/pages/Chat';
import DirectorDashboard from '@/pages/director/DirectorDashboard';
import Expenses from '@/pages/director/Expenses';
import Partners from '@/pages/director/Partners';
import PartnerCard from '@/pages/director/PartnerCard';

function Boot({ children }: { children: ReactNode }) {
  const hydrated = useAuthHydrated();
  if (!hydrated) {
    return (
      <div className="min-h-dvh grid place-items-center bg-paper text-ink/50 text-sm">Загрузка…</div>
    );
  }
  return children;
}

function Protected({ children }: { children: ReactElement }) {
  const token = useAuthStore((s) => s.accessToken);
  const loc = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return children;
}

function GuestOnly({ children }: { children: ReactElement }) {
  const token = useAuthStore((s) => s.accessToken);
  if (token) return <Navigate to="/" replace />;
  return children;
}

/** Раздел главного менеджера: роль сняли — раздел пропадает. */
function HeadOnly({ children }: { children: ReactElement }) {
  const isHead = useIsHeadManager();
  if (!isHead) return <Navigate to="/" replace />;
  return children;
}

/** Финансы отдела: роль сняли — раздел пропадает. */
function DirectorOnly({ children }: { children: ReactElement }) {
  const isDirector = useIsSalesDirector();
  if (!isDirector) return <Navigate to="/" replace />;
  return children;
}

/** На «Обзоре» руководитель видит отдел целиком, остальные — свои начисления. */
function Home() {
  const isDirector = useIsSalesDirector();
  return isDirector ? <DirectorDashboard /> : <Dashboard />;
}

export default function App() {
  return (
    <Boot>
      <ScrollManager />
      <InstallPrompt />
      <Routes>
        <Route
          path="/login"
          element={
            <GuestOnly>
              <Login />
            </GuestOnly>
          }
        />
        <Route
          path="/*"
          element={
            <Protected>
              <Layout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/establishments" element={<Establishments />} />
                  <Route path="/establishments/:id" element={<EstablishmentDetail />} />
                  <Route
                    path="/managers"
                    element={
                      <HeadOnly>
                        <Managers />
                      </HeadOnly>
                    }
                  />
                  <Route
                    path="/managers/:id"
                    element={
                      <HeadOnly>
                        <ManagerCard />
                      </HeadOnly>
                    }
                  />
                  <Route path="/me" element={<MyCabinet />} />
                  <Route
                    path="/expenses"
                    element={
                      <DirectorOnly>
                        <Expenses />
                      </DirectorOnly>
                    }
                  />
                  {/* Поставщики и оптовики — руководителю, только просмотр. key — свежий экран при переходе между ними. */}
                  <Route
                    path="/suppliers"
                    element={
                      <DirectorOnly>
                        <Partners key="supplier" kind="supplier" />
                      </DirectorOnly>
                    }
                  />
                  <Route
                    path="/suppliers/:id"
                    element={
                      <DirectorOnly>
                        <PartnerCard key="supplier" kind="supplier" />
                      </DirectorOnly>
                    }
                  />
                  <Route
                    path="/wholesalers"
                    element={
                      <DirectorOnly>
                        <Partners key="wholesaler" kind="wholesaler" />
                      </DirectorOnly>
                    }
                  />
                  <Route
                    path="/wholesalers/:id"
                    element={
                      <DirectorOnly>
                        <PartnerCard key="wholesaler" kind="wholesaler" />
                      </DirectorOnly>
                    }
                  />
                  <Route path="/chat" element={<Chat />} />
                  <Route path="/payouts" element={<Payouts />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            </Protected>
          }
        />
      </Routes>
    </Boot>
  );
}
